import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Clock, Mail, CheckCircle, Loader2, ShoppingCart, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DCEOrderModal({ isOpen, onClose, tender }) {
  const { token, user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPacks();
    }
  }, [isOpen]);

  const fetchPacks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dce/packs`);
      if (response.ok) {
        const data = await response.json();
        setPacks(data);
        if (data.length > 0) {
          setSelectedPack(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching packs:', err);
    }
  };

  const handleCheckout = async () => {
    if (!token) {
      setError("Veuillez vous connecter pour commander un DCE");
      return;
    }

    if (!selectedPack) {
      setError("Veuillez sélectionner un pack");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/dce/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pack_id: selectedPack,
          tender_id: tender.id,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Erreur lors de la création du paiement");
      }
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("URL de paiement non reçue");
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const selectedPackData = packs.find(p => p.id === selectedPack);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-violet-600" />
            Commander le DCE
          </DialogTitle>
          <DialogDescription>
            Recevez le Dossier de Consultation des Entreprises par email sous 24h
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tender Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-500 mb-1">Marché sélectionné</p>
            <p className="font-medium text-slate-900 line-clamp-2">{tender?.title}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                {tender?.source}
              </Badge>
              <span className="text-xs text-slate-500">{tender?.reference}</span>
            </div>
          </div>

          {/* Pack Selection */}
          <div className="space-y-3">
            <p className="font-medium text-slate-900">Choisissez votre pack</p>
            
            {packs.map((pack) => (
              <button
                key={pack.id}
                onClick={() => setSelectedPack(pack.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  selectedPack === pack.id 
                    ? 'border-violet-600 bg-violet-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPack === pack.id ? 'border-violet-600 bg-violet-600' : 'border-slate-300'
                    }`}>
                      {selectedPack === pack.id && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{pack.name}</p>
                      <p className="text-sm text-slate-500">{pack.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-violet-600">{pack.price.toFixed(0)}€</p>
                    {pack.quantity > 1 && (
                      <p className="text-xs text-green-600">
                        {(pack.price / pack.quantity).toFixed(2)}€/DCE
                      </p>
                    )}
                  </div>
                </div>
                {pack.id === 'pack_10' && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                    <Sparkles className="w-3 h-3" />
                    <span>Meilleur rapport qualité-prix</span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-violet-500" />
              <span>Livraison sous 24h</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4 text-violet-500" />
              <span>Envoi par email</span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Login prompt */}
          {!token && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-700 text-sm">
                Vous devez être connecté pour commander un DCE.
              </p>
            </div>
          )}

          {/* Checkout Button */}
          <Button
            onClick={handleCheckout}
            disabled={loading || !token || !selectedPack}
            className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base"
            data-testid="dce-checkout-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirection vers le paiement...
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4 mr-2" />
                Payer {selectedPackData?.price.toFixed(0)}€ - {selectedPackData?.name}
              </>
            )}
          </Button>

          <p className="text-xs text-center text-slate-500">
            Paiement sécurisé par Stripe. Vous serez redirigé vers une page de paiement sécurisée.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
